// File: frontend/src/pages/attendance/AttendanceSummary.jsx

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSchool } from '../../context/SchoolContext';
import api from '../../utils/axios';

const AttendanceSummary = () => {
  const { school } = useSchool();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetch attendance summary and class-wise breakdown from API
   */
  const fetchSummary = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get('/api/v1/attendance/summary', {
        params: { date: selectedDate }
      });

      if (response.data?.success) {
        setSummaryData(response.data.data);
      } else {
        setError(response.data?.message || 'Failed to fetch summary data');
      }
    } catch (err) {
      console.error('Error fetching attendance summary:', err);
      setError(err.response?.data?.message || err.message || 'Error communicating with server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top Header Navbar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard/admin" className="w-10 h-10 rounded-xl overflow-hidden bg-white border border-slate-200 p-1 flex items-center justify-center shadow-sm">
              {school?.logo ? (
                <img src={school.logo} alt="Logo" className="max-h-full max-w-full object-contain" />
              ) : (
                <span className="font-extrabold text-primary text-sm">SMS</span>
              )}
            </Link>
            <div>
              <h1 className="font-bold text-slate-800 text-lg leading-tight">Attendance Summary</h1>
              <p className="text-xs text-slate-500">Real-time attendance metrics & class breakdown</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/attendance/mark"
              className="bg-primary hover:bg-blue-600 text-white font-medium px-4 py-2 rounded-xl text-sm transition shadow-sm"
            >
              Mark Attendance
            </Link>
            <Link
              to="/attendance/report"
              className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium px-4 py-2 rounded-xl text-sm transition"
            >
              Monthly Reports
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Date Filter & Refresh Header */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h2 className="font-bold text-slate-800 text-lg">Daily Attendance Overview</h2>
            <p className="text-xs text-slate-500">Select a date to view attendance stats for that day</p>
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto">
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary shadow-sm"
            />
            <button
              onClick={fetchSummary}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-xl text-sm transition"
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl text-sm flex items-center justify-between shadow-sm">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="font-bold text-red-800">✕</button>
          </div>
        )}

        {/* 4 Stat Overview Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          
          {/* Card 1: Total Students */}
          <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-xl bg-blue-50 text-primary flex items-center justify-center font-bold">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full">School Roster</span>
            </div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total Students</p>
            <h3 className="text-3xl font-extrabold text-slate-900 mt-1">
              {loading ? '...' : summaryData?.total_students || 0}
            </h3>
          </div>

          {/* Card 2: Present Today */}
          <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full">
                {summaryData?.percentage_today || 0}% Rate
              </span>
            </div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Present Today</p>
            <h3 className="text-3xl font-extrabold text-emerald-600 mt-1">
              {loading ? '...' : summaryData?.present_today || 0}
            </h3>
          </div>

          {/* Card 3: Absent Today */}
          <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-xl bg-red-50 text-red-600 flex items-center justify-center font-bold">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span className="text-xs font-bold text-red-700 bg-red-50 px-2.5 py-1 rounded-full">Requires Attention</span>
            </div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Absent Today</p>
            <h3 className="text-3xl font-extrabold text-red-600 mt-1">
              {loading ? '...' : summaryData?.absent_today || 0}
            </h3>
          </div>

          {/* Card 4: Late Today */}
          <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center font-bold">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span className="text-xs font-bold text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full">Late Arrivals</span>
            </div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Late Today</p>
            <h3 className="text-3xl font-extrabold text-amber-600 mt-1">
              {loading ? '...' : summaryData?.late_today || 0}
            </h3>
          </div>

        </div>

        {/* Class-wise Breakdown Table */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-6">
          <div className="p-4 bg-slate-50 border-b border-slate-200">
            <h3 className="font-bold text-slate-800 text-sm">Class-wise Attendance Breakdown</h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-6 py-3.5">Class Name</th>
                  <th className="px-6 py-3.5">Total Students</th>
                  <th className="px-6 py-3.5 text-emerald-700">Present</th>
                  <th className="px-6 py-3.5 text-red-700">Absent</th>
                  <th className="px-6 py-3.5 text-amber-700">Late</th>
                  <th className="px-6 py-3.5 text-blue-700">Leave</th>
                  <th className="px-6 py-3.5 text-right">Attendance Rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm font-medium text-slate-700">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-slate-400">
                      <div className="flex flex-col items-center gap-2">
                        <svg className="w-8 h-8 animate-spin text-primary" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Loading class breakdown...</span>
                      </div>
                    </td>
                  </tr>
                ) : !summaryData?.class_breakdown || summaryData.class_breakdown.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-slate-500">
                      No attendance data recorded for {selectedDate}.
                    </td>
                  </tr>
                ) : (
                  summaryData.class_breakdown.map((row) => {
                    const totalClass = row.total_students || 0;
                    const presentClass = row.present_count || 0;
                    const rate = totalClass > 0 ? Math.round((presentClass / totalClass) * 100) : 0;

                    return (
                      <tr key={row.class_id} className="hover:bg-slate-50/80 transition">
                        <td className="px-6 py-4 font-bold text-slate-900">
                          Class {row.class_id}
                        </td>
                        <td className="px-6 py-4 font-semibold text-slate-700">
                          {totalClass}
                        </td>
                        <td className="px-6 py-4 font-bold text-emerald-600">
                          {row.present_count || 0}
                        </td>
                        <td className="px-6 py-4 font-bold text-red-600">
                          {row.absent_count || 0}
                        </td>
                        <td className="px-6 py-4 font-bold text-amber-600">
                          {row.late_count || 0}
                        </td>
                        <td className="px-6 py-4 font-bold text-blue-600">
                          {row.leave_count || 0}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className={`px-3 py-1 rounded-full text-xs font-extrabold uppercase tracking-wider ${rate >= 90 ? 'bg-emerald-100 text-emerald-800' : rate >= 75 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`}>
                            {rate}%
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

      </main>
    </div>
  );
};

export default AttendanceSummary;
